import { consola } from 'consola'
import { useApi } from '@shared/api'
import type {
  ReferralSettingsResponseDto,
  CreateReferralSettingsDto,
  UpdateReferralSettingsDto,
} from '@entities/referral'

const logger = consola.withTag('api-referral-admin')

export interface ReferralProgramStats {
  totalReferrers: number
  totalReferrals: number
  activeReferrals: number
  completedReferrals: number
  totalRewardsDistributed: number
  totalRewardsCount: number
  totalRevenueFromReferrals: number
  conversionToCustomers: number
  conversionToCompleted: number
  totalUsers?: number
  totalRewardsGiven?: number
  totalRevenue?: number
  conversionRate?: number
}

export interface UserReferralCode {
  id: string
  userId: string
  userName: string
  userPhone?: string
  code: string
  referralToken: string
  usageCount: number
  referralsCount?: number
  isActive: boolean
  createdAt: string
}

export interface ReferralDetails {
  id: string
  referrerId: string
  referrerName: string
  refereeId: string
  refereeName: string
  referralCode: string
  referralToken?: string | null
  status: string
  createdAt: string
  expiresAt: string | null
  firstOrderDate?: string | null
  refereeOrdersCount: number
  totalOrders?: number
  refereeRevenue: number
  totalRevenue?: number
  referrerRewarded: boolean
  refereeRewarded: boolean
  referrerRewardAmount: number | null
  refereeRewardAmount: number | null
  rewardedAt?: string | null
  refereeIpAddress?: string | null
  refereeDeviceId?: string | null
}

export interface PaginationParams {
  page?: number
  limit?: number
}

export interface AdminReferralPaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  limit: number
  totalPages: number
  data?: T[]
}

class APIAdminReferral {
  async getProgramStats(): Promise<ReferralProgramStats> {
    const { data } = await useApi(true, true).get('/admin/referral/stats')
    logger.debug('Program stats fetched')
    return {
      ...data,
      totalUsers: data.totalReferrers,
      totalRewardsGiven: data.totalRewardsDistributed,
      totalRevenue: data.totalRevenueFromReferrals,
      conversionRate: data.conversionToCustomers,
    }
  }

  async getAllReferralCodes(params?: PaginationParams): Promise<AdminReferralPaginatedResponse<UserReferralCode>> {
    const { data } = await useApi(true, true).get('/admin/referral/codes', {
      params: { page: params?.page ?? 1, limit: params?.limit ?? 20 },
    })
    return { ...data, data: data.items ?? data.data ?? [], items: data.items ?? data.data ?? [] }
  }

  async getAllReferrals(params?: PaginationParams): Promise<AdminReferralPaginatedResponse<ReferralDetails>> {
    const { data } = await useApi(true, true).get('/admin/referral/referrals', {
      params: { page: params?.page ?? 1, limit: params?.limit ?? 20 },
    })
    return { ...data, data: data.items ?? data.data ?? [], items: data.items ?? data.data ?? [] }
  }

  async toggleReferralCode(userId: string, isActive: boolean): Promise<{ success: boolean; message: string }> {
    const { data } = await useApi(true, true).put(`/admin/referral/codes/${userId}/toggle`, { body: { isActive } })
    return data
  }

  async getUserAnalytics(userId: string): Promise<any> {
    const { data } = await useApi(true, true).get(`/admin/referral/user/${userId}/analytics`)
    return data
  }

  async getSettings(): Promise<ReferralSettingsResponseDto | null> {
    const { data } = await useApi(true, true).get('/admin/referral/settings')
    return data
  }

  async createSettings(payload: CreateReferralSettingsDto): Promise<ReferralSettingsResponseDto> {
    const { data } = await useApi(true, true).post('/admin/referral/settings', { body: payload })
    return data
  }

  async updateSettings(payload: UpdateReferralSettingsDto): Promise<ReferralSettingsResponseDto> {
    const { data } = await useApi(true, true).put('/admin/referral/settings', { body: payload })
    return data
  }
}

export const apiAdminReferral = new APIAdminReferral()
export default apiAdminReferral
